// src/features/home/screens/HomeScreen.tsx

import React, { useRef, useEffect } from 'react';
import styles from './HomeScreen.module.scss';
import Sidebar from '../components/Sidebar';
import MainContent from '../components/MainContent';
import { useActiveSection } from '../../../shared/context/ActiveSectionContext';

const HomeScreen: React.FC = () => {
  const aboutRef = useRef<HTMLDivElement>(null);
  const experienceRef = useRef<HTMLDivElement>(null);
  const projectsRef = useRef<HTMLDivElement>(null);

  const { activeSection, setActiveSection } = useActiveSection();

  useEffect(() => {
    const handleScroll = () => {
      // Get header height (if your header has a fixed height)
      const header = document.querySelector('.header');
      const headerHeight = header?.clientHeight || 0;
      // Adjust scroll position: add headerHeight and a small offset (e.g., 20px) for better detection.
      const scrollPos = window.scrollY + headerHeight + 20;

      // Prepare an array of sections with their offsetTop values.
      const sections = [
        { name: 'about', offset: aboutRef.current?.offsetTop || 0 },
        { name: 'experience', offset: experienceRef.current?.offsetTop || 0 },
        { name: 'projects', offset: projectsRef.current?.offsetTop || 0 },
      ];

      // (Optional) sort sections by offset in ascending order.
      sections.sort((a, b) => a.offset - b.offset);

      // Loop through sections to find the last section whose offset is less than or equal to scrollPos.
      let newActiveSection = sections[0].name;
      for (let i = 0; i < sections.length; i++) {
        if (scrollPos >= sections[i].offset) {
          newActiveSection = sections[i].name;
        }
      }
      setActiveSection(newActiveSection);
    };

    window.addEventListener('scroll', handleScroll);
    // Trigger once on mount
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [setActiveSection]);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    const header = document.querySelector('.header');
    const headerHeight = header?.clientHeight || 0;
    const offsetTop = ref.current?.offsetTop || 0;
    
    // Match the scroll detection logic exactly:
    // The scroll detection considers a section active when:
    // window.scrollY + headerHeight + 20 >= section.offsetTop
    // So to position correctly, we need:
    // window.scrollY + headerHeight + 20 = offsetTop
    // Therefore: window.scrollY = offsetTop - headerHeight - 20
    
    window.scrollTo({
      top: offsetTop - headerHeight - 20,
      behavior: 'smooth',
    });
  };

  return (
    <div className={styles.homeScreen}>
      <Sidebar
        aboutRef={aboutRef}
        experienceRef={experienceRef}
        projectsRef={projectsRef}
        activeSection={activeSection}
        scrollToSection={scrollToSection}
      />
      <MainContent
        aboutRef={aboutRef}
        experienceRef={experienceRef}
        projectsRef={projectsRef}
      />
    </div>
  );
};

export default HomeScreen;